/**
 * A small history-API router.
 *
 *   defineRoutes([{ path: '/student/course/:id', ...definition }])
 *   navigate('/student/dashboard')
 *
 * The router only matches URLs and manages history. What to do with a match (guards, loading data,
 * rendering) belongs to the app shell, which subscribes with onNavigate().
 */

const routes = [];
const listeners = new Set();
let current = { path: '/', params: {}, query: new URLSearchParams(), def: null, url: '/' };

function compile(path) {
  const keys = [];
  const source = path
    .split('/')
    .map((part) => {
      if (part.startsWith(':')) {
        keys.push(part.slice(1));
        return '([^/]+)';
      }
      if (part === '*') {
        keys.push('rest');
        return '(.*)';
      }
      return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('/');
  return { regex: new RegExp(`^${source}/?$`), keys };
}

export function defineRoutes(list) {
  for (const def of list) routes.push({ def, ...compile(def.path) });
}

export function clearRoutes() {
  routes.length = 0;
}

/** Find the route for a pathname. Static paths win over patterns because they are listed first. */
export function matchRoute(pathname) {
  for (const { def, regex, keys } of routes) {
    const m = regex.exec(pathname);
    if (m) {
      const params = {};
      keys.forEach((key, i) => {
        try {
          params[key] = decodeURIComponent(m[i + 1]);
        } catch {
          params[key] = m[i + 1];
        }
      });
      return { def, params };
    }
  }
  return null;
}

export function resolve(url) {
  const parsed = new URL(url, location.origin);
  const hit = matchRoute(parsed.pathname) || { def: null, params: {} };
  return { path: parsed.pathname, query: parsed.searchParams, hash: parsed.hash, url: parsed.pathname + parsed.search + parsed.hash, ...hit };
}

export function currentRoute() {
  return current;
}

async function apply(url, { replace = false, push = true } = {}) {
  const next = resolve(url);
  if (push) {
    const fn = replace ? 'replaceState' : 'pushState';
    if (next.url !== location.pathname + location.search + location.hash) history[fn](null, '', next.url);
  }
  const previous = current;
  current = next;
  for (const fn of listeners) await fn(next, previous);
}

export function navigate(url, options) {
  return apply(url, options);
}

export function redirect(url) {
  return apply(url, { replace: true });
}

/** Subscribe to navigations. The callback receives (next, previous). Returns an unsubscribe function. */
export function onNavigate(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function startRouter() {
  window.addEventListener('popstate', () => apply(location.pathname + location.search + location.hash, { push: false }));
  return apply(location.pathname + location.search + location.hash, { push: false });
}

/** Re-run the current route (used after login or a data change that affects guards). */
export function refreshRoute() {
  return apply(current.url, { push: false });
}
